import { useState } from 'react';
import { type FineReason, formatRubles, useFineReasons, useSaveFineReasons } from '../api/records';
import { Icon } from './Icon';
import { ErrorState, LoadingState } from './states';

interface DraftRow {
  key: number;
  title: string;
  amount: string;
}

let nextKey = 1;
const toDraft = (items: FineReason[]): DraftRow[] =>
  items.map((item) => ({ key: nextKey++, title: item.title, amount: String(item.amountKopecks / 100) }));

/** Причины штрафов группы должностей: видит HR, меняет HR всей компании. */
export function FineReasonsBlock({
  roleGroupId,
  membersCount,
}: {
  roleGroupId: string;
  membersCount: number;
}) {
  const reasons = useFineReasons(roleGroupId);
  const save = useSaveFineReasons(roleGroupId);
  const [draft, setDraft] = useState<DraftRow[] | null>(null);

  const parsed = (draft ?? [])
    .map((row) => ({
      title: row.title.replace(/\s+/g, ' ').trim(),
      amountKopecks: Math.round(Number(row.amount.replace(/\s/g, '').replace(',', '.')) * 100),
    }))
    .filter((row) => row.title);
  const invalid = parsed.some((row) => !Number.isFinite(row.amountKopecks) || row.amountKopecks <= 0);
  const update = (key: number, patch: Partial<DraftRow>) =>
    setDraft((rows) => rows?.map((row) => (row.key === key ? { ...row, ...patch } : row)) ?? null);

  return (
    <section className="panel">
      <div className="panel-header">
        <h2>Виды нарушений</h2>
        <div className="panel-actions">
          {draft ? (
            <>
              <button type="button" className="btn-secondary" onClick={() => setDraft(null)}>
                Отмена
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={save.isPending || invalid}
                onClick={() => save.mutate(parsed, { onSuccess: () => setDraft(null) })}
              >
                Сохранить для {membersCount} сотрудников
              </button>
            </>
          ) : reasons.data?.canEdit ? (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDraft(toDraft(reasons.data.items))}
            >
              <Icon name="pen" size={14} />
              Изменить
            </button>
          ) : null}
        </div>
      </div>
      {reasons.isPending ? <LoadingState /> : null}
      {reasons.isError ? <ErrorState error={reasons.error} /> : null}
      {draft ? (
        <div className="reg-edit-list">
          {draft.map((row, index) => (
            <div key={row.key} className="edit-row fine-edit-row">
              <input
                className="input"
                aria-label={`Причина ${index + 1}`}
                value={row.title}
                placeholder="Нарушение"
                onChange={(event) => update(row.key, { title: event.target.value })}
              />
              <input
                className="input fine-amount"
                aria-label={`Сумма ${index + 1}`}
                inputMode="decimal"
                value={row.amount}
                placeholder="Сумма, ₽"
                onChange={(event) => update(row.key, { amount: event.target.value })}
              />
              <button
                type="button"
                className="btn-icon ghost"
                aria-label={`Удалить причину ${index + 1}`}
                onClick={() => setDraft((rows) => rows?.filter((item) => item.key !== row.key) ?? null)}
              >
                <Icon name="trash" size={14} />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setDraft((rows) => [...(rows ?? []), { key: nextKey++, title: '', amount: '' }])}
          >
            <Icon name="plus" size={14} />
            Добавить причину
          </button>
          {invalid ? <p className="text-bad">Сумма должна быть положительным числом</p> : null}
          {save.isError ? <ErrorState title="Причины не сохранены" error={save.error} /> : null}
        </div>
      ) : reasons.data?.items.length ? (
        <ol className="reg-list-rows">
          {reasons.data.items.map((reason, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: список только для чтения, порядок задаёт HR
            <li key={index}>
              <span className="reg-num">{index + 1}</span>
              <span className="reg-text">{reason.title}</span>
              <span className="reg-period">{formatRubles(reason.amountKopecks)}</span>
            </li>
          ))}
        </ol>
      ) : reasons.data ? (
        <p className="muted">Причины не заполнены</p>
      ) : null}
    </section>
  );
}
